import React, { ChangeEvent, Fragment, useEffect, useState } from 'react'
import { FormikTextInputProps } from '@/shared/forms/components/FormikTextInput'
import { useDebounce } from '@uidotdev/usehooks'
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  Transition,
} from '@headlessui/react'
import { useField, useFormikContext } from 'formik'
import { SizeVariants } from '@/shared/forms/components/constants'
import clsx from 'clsx'
import { GooglePlacesAutocompleteResult } from '@/lib/apis/types'
import {
  useGoogleServices,
  usePlacesAutocompleteService,
  usePlacesDetailsService,
} from '@/shared/hooks/GoogleServiceQueries'
import Spinner from '@/shared/components/Spinner'
import { DetailedAddress } from '@/lib/types'

type Props = {
  onPickAddress: (address: DetailedAddress) => void
} & FormikTextInputProps

export default function GooglePlacesAutocompleteInput({
  label,
  size,
  autocomplete,
  disabled,
  placeholder,

  optional = false,
  onPickAddress,
  inputClassname,
  appearance = 'default',
  ...props
}: Props) {
  const [field, meta] = useField(props)
  const { isSubmitting } = useFormikContext()
  const services = useGoogleServices()
  const [input, setInput] = useState('')
  const [selectedPlace, setSelectedPlace] =
    useState<GooglePlacesAutocompleteResult>()
  const debouncedInput = useDebounce(input, 200)
  const predictions = usePlacesAutocompleteService(services, debouncedInput)
  const inputId = props.id ?? field.name

  const placeDetailsQuery = usePlacesDetailsService(
    services,
    selectedPlace ? selectedPlace.placeId : '',
  )
  useEffect(() => {
    if (
      !placeDetailsQuery.isLoading &&
      selectedPlace &&
      placeDetailsQuery.data
    ) {
      const data = placeDetailsQuery.data
      const suffix = `, ${data.suburb} ${data.state}`
      const formattedAddress = selectedPlace.description.replace(suffix, '')

      onPickAddress({ formattedAddress, ...placeDetailsQuery.data })
    }
  }, [placeDetailsQuery.isLoading, placeDetailsQuery.data])

  const isDisabled = disabled || isSubmitting

  function handleChange(e: ChangeEvent<any>) {
    setInput(e.target.value)
    field.onChange(e)
  }

  async function handlePickAddress(
    value: GooglePlacesAutocompleteResult | null,
  ) {
    if (value) {
      setInput(value.description)
      setSelectedPlace(value)
    }
  }

  let addressesDropdown
  if (predictions.isLoading) {
    addressesDropdown = (
      <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
        <div className="flex w-full justify-center">
          <Spinner />
        </div>
      </div>
    )
  } else if (!predictions.data || predictions.data.length === 0) {
    addressesDropdown = (
      <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
        No address found.
      </div>
    )
  } else {
    addressesDropdown = (
      <>
        {predictions.data?.map((prediction) => (
          <ComboboxOption
            key={prediction.placeId}
            value={prediction}
            className={({ focus }) =>
              `relative cursor-pointer select-none py-1.5 pl-2.5 pr-4 sm:leading-6 ${
                focus ? 'bg-blue-600 text-white' : 'text-gray-900'
              }`
            }
          >
            {prediction.description}
          </ComboboxOption>
        ))}
      </>
    )
  }

  return (
    <div className={SizeVariants[size as keyof typeof SizeVariants]}>
      <div className="flex justify-between">
        <label
          htmlFor={inputId}
          className={clsx(
            'block text-sm font-medium leading-6',
            isDisabled ? 'text-gray-500' : 'text-gray-900',
          )}
        >
          {label}
        </label>
        {optional && (
          <span className="text-sm leading-6 text-gray-500" id="email-optional">
            Optional
          </span>
        )}
      </div>
      <div className="relative mt-2">
        <Combobox onChange={handlePickAddress}>
          <ComboboxInput
            type="text"
            autoComplete={autocomplete}
            className={clsx(
              appearance === 'portal-profile'
                ? 'block h-[52px] w-full rounded-[14px] border-[1.5px] bg-[#F4F4F6] px-4 py-0 text-[15px] leading-5 text-[#0B0B0F] outline-none transition-colors duration-300 placeholder:text-[#A0A0A8] focus:border-[#0B0B0F]/45'
                : 'block h-10 w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6',
              meta.touched && meta.error
                ? appearance === 'portal-profile'
                  ? 'border-[#C6453C]'
                  : 'ring-red-600'
                : appearance === 'portal-profile'
                  ? 'border-transparent'
                  : 'ring-gray-300',
              isDisabled
                ? appearance === 'portal-profile'
                  ? 'cursor-not-allowed opacity-60'
                  : 'bg-slate-300'
                : '',
              inputClassname,
            )}
            id={inputId}
            value={field.value}
            name={field.name}
            onChange={handleChange}
            onBlur={field.onBlur}
            placeholder={placeholder}
          />
          {debouncedInput.length >= 3 && (
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {addressesDropdown}
              </Combobox.Options>
            </Transition>
          )}
        </Combobox>
        {meta.touched && meta.error && (
          <div className="mt-2 text-sm font-semibold text-red-600">
            {meta.error}
          </div>
        )}
      </div>
    </div>
  )
}
